// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * UIContext
 * 
 * Centralized UI state management for modals, panels, and UI interactions
 * Handles modal stacking, z-index management, and UI state
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const UIContext = createContext();

// Modal types
export const MODAL_TYPES = {
  SYSTEM_STATUS: 'systemStatus',
  ALERT_HISTORY: 'alertHistory',
  RESET_CONFIRM: 'resetConfirm',
  SAVE_LOAD: 'saveLoad',
  CHEATS: 'cheats',
  COURSE: 'course',
  WARP_GATE: 'warpGate',
  TRADE_SHOP: 'tradeShop',
  WEAPON_SHOP: 'weaponShop',
  UPGRADE_SHOP: 'upgradeShop',
  MODULES_SHOP: 'modulesShop',
  PLANET_EXPLORATION: 'planetExploration',
  ALIEN_ENCOUNTER: 'alienEncounter',
  COMBAT: 'combat',
  DOCK_SCREEN: 'dockScreen',
  ARRIVAL_SCREEN: 'arrivalScreen',
  HELP: 'help',
  REPUTATION: 'reputation',
  SETTINGS: 'settings',
  ACHIEVEMENTS: 'achievements',
  LOGIN: 'login',
  DEV_TOOLS: 'devTools'
};

export const UIProvider = ({ children }) => {
  // Modal stack (array of modal objects)
  const [modalStack, setModalStack] = useState([]);
  
  // Z-index counter for stacking
  const zIndexCounter = useRef(1000);
  
  // Cheat message state
  const [cheatMessage, setCheatMessage] = useState(null);
  
  // Portal state
  const [isPortalActive, setIsPortalActive] = useState(false);

  /**
   * Open a modal
   * @param {string} type - Modal type from MODAL_TYPES
   * @param {object} props - Props to pass to the modal
   * @param {boolean} closeOthers - Whether to close other modals first
   * @returns {string} Modal ID
   */
  const openModal = useCallback((type, props = {}, closeOthers = false) => {
    setModalStack(prev => {
      // Prevent duplicate same-type modals
      if (!closeOthers && prev.some(m => m.type === type)) return prev;
      const modalId = `modal-${type}-${Date.now()}`;
      const newModal = {
        id: modalId,
        type,
        props,
        zIndex: zIndexCounter.current++,
        timestamp: Date.now()
      };
      if (closeOthers) return [newModal];
      return [...prev, newModal];
    });
  }, []);

  /**
   * Close a modal by ID
   * @param {string} modalId - Modal ID to close
   */
  const closeModal = useCallback((modalId) => {
    setModalStack(prev => prev.filter(modal => modal.id !== modalId));
  }, []);

  /**
   * Close a modal by type
   * @param {string} type - Modal type to close
   */
  const closeModalByType = useCallback((type) => {
    setModalStack(prev => prev.filter(modal => modal.type !== type));
  }, []);

  /**
   * Close all modals
   */
  const closeAllModals = useCallback(() => {
    setModalStack([]);
    zIndexCounter.current = 1000;
  }, []);

  /**
   * Check if a modal is open
   * @param {string} type - Modal type to check
   * @returns {boolean}
   */
  const isModalOpen = useCallback((type) => {
    return modalStack.some(modal => modal.type === type);
  }, [modalStack]);

  /**
   * Get modal by type
   * @param {string} type - Modal type
   * @returns {object|null} Modal object or null
   */
  const getModal = useCallback((type) => {
    return modalStack.find(modal => modal.type === type) || null;
  }, [modalStack]);

  /**
   * Get top modal (highest z-index)
   * @returns {object|null} Modal object or null
   */
  const getTopModal = useCallback(() => {
    if (modalStack.length === 0) return null;
    return modalStack.reduce((top, modal) => 
      modal.zIndex > top.zIndex ? modal : top
    );
  }, [modalStack]);

  /**
   * Update modal props
   * @param {string} modalId - Modal ID
   * @param {object} newProps - New props to merge
   */
  const updateModalProps = useCallback((modalId, newProps) => {
    setModalStack(prev => 
      prev.map(modal => 
        modal.id === modalId 
          ? { ...modal, props: { ...modal.props, ...newProps } }
          : modal
      )
    );
  }, []);

  /**
   * Show cheat message
   * @param {string} message - Message to display
   * @param {number} duration - Duration in ms (default 2000)
   */
  const showCheatMessage = useCallback((message, duration = 2000) => {
    setCheatMessage(message);
    setTimeout(() => setCheatMessage(null), duration);
  }, []);

  /**
   * Activate portal effect
   * @param {number} duration - Duration in ms (default 5000)
   */
  const activatePortal = useCallback((duration = 5000) => {
    setIsPortalActive(true);
    setTimeout(() => setIsPortalActive(false), duration);
  }, []);

  /**
   * Convenience methods for common modals
   */
  const openSystemStatus = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.SYSTEM_STATUS, props, false);
  }, [openModal]);

  const openAlertHistory = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.ALERT_HISTORY, props, false);
  }, [openModal]);

  const openResetConfirm = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.RESET_CONFIRM, props, false);
  }, [openModal]);

  const openSaveLoad = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.SAVE_LOAD, props, false);
  }, [openModal]);

  const openCheats = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.CHEATS, props, false);
  }, [openModal]);

  const openHelp = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.HELP, props, false);
  }, [openModal]);

  const openReputation = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.REPUTATION, props, false);
  }, [openModal]);

  const openSettings = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.SETTINGS, props, false);
  }, [openModal]);

  const openAchievements = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.ACHIEVEMENTS, props, false);
  }, [openModal]);

  const openLogin = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.LOGIN, props, false);
  }, [openModal]);

  const openDevTools = useCallback((props = {}) => {
    return openModal(MODAL_TYPES.DEV_TOOLS, props, false);
  }, [openModal]);

  const value = {
    // State
    modalStack,
    cheatMessage,
    isPortalActive,
    
    // Modal actions
    openModal,
    closeModal,
    closeModalByType,
    closeAllModals,
    isModalOpen,
    getModal,
    getTopModal,
    updateModalProps,
    
    // Convenience methods
    openSystemStatus,
    openAlertHistory,
    openResetConfirm,
    openSaveLoad,
    openCheats,
    openHelp,
    openReputation,
    openSettings,
    openAchievements,
    openLogin,
    openDevTools,
    
    // UI actions
    showCheatMessage,
    activatePortal,
    
    // Constants
    MODAL_TYPES
  };

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

/**
 * Hook to use UI context
 */
export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

export default UIContext;
